import { useForm } from "react-hook-form";
import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrganization, useUpdateOrganization, useOrganizationStats } from "@/hooks/useOrganization";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, ShoppingCart } from "lucide-react";

const organizationSchema = z.object({
  name: z.string().min(2).max(100),
  timezone: z.string(),
  volume_unit: z.enum(["liters", "gallons", "barrels"]),
  temperature_unit: z.enum(["celsius", "fahrenheit"]),
  date_format: z.string(),
});

function PartsPurchaserCard({ org, updateOrg }: { org: any; updateOrg: any }) {
  const settings = (org?.settings as any) || {};
  const [name, setName] = useState(settings.parts_purchaser_name || "");
  const [email, setEmail] = useState(settings.parts_purchaser_email || "");
  const [dirty, setDirty] = useState(false);

  const handleSave = () => {
    updateOrg.mutate({
      settings: {
        ...settings,
        parts_purchaser_name: name.trim() || null,
        parts_purchaser_email: email.trim() || null,
      },
    }, { onSuccess: () => setDirty(false) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          Parts Purchaser
        </CardTitle>
        <CardDescription>
          The person who receives reorder requests when parts run low. Used on printed part labels.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); setDirty(true); }}
              placeholder="e.g. Jane Smith"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setDirty(true); }}
              placeholder="jane@gravitywinery.com"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={updateOrg.isPending || !dirty} size="sm">
            {updateOrg.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function OrganizationTab() {
  const { data: org, isLoading } = useOrganization();
  const { data: stats } = useOrganizationStats();
  const updateOrg = useUpdateOrganization();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !org) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please upload an image file (PNG, JPG, SVG)", variant: "destructive" });
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${org.id}/logo.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("org-logos")
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("org-logos").getPublicUrl(path);
      await updateOrg.mutateAsync({ logo_url: urlData.publicUrl });
      toast({ title: "Logo updated" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    if (!org) return;
    await updateOrg.mutateAsync({ logo_url: "" });
    toast({ title: "Logo removed" });
  };

  const settings = (org?.settings as any) || {};
  
  const form = useForm<z.infer<typeof organizationSchema>>({
    resolver: zodResolver(organizationSchema),
    values: {
      name: org?.name || "",
      timezone: settings.timezone || "UTC",
      volume_unit: settings.volume_unit || "gallons",
      temperature_unit: settings.temperature_unit || "celsius",
      date_format: settings.date_format || "MM/DD/YYYY",
    },
  });

  const onSubmit = (data: z.infer<typeof organizationSchema>) => {
    updateOrg.mutate({
      name: data.name,
      settings: {
        timezone: data.timezone,
        volume_unit: data.volume_unit,
        temperature_unit: data.temperature_unit,
        date_format: data.date_format,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
          <CardDescription>
            Manage your organization's basic information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                {...form.register("name")}
                placeholder="Enter organization name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Member Count</Label>
                <div className="text-2xl font-bold">{stats?.memberCount || 0}</div>
                <p className="text-sm text-muted-foreground">Active members</p>
              </div>
              <div className="space-y-2">
                <Label>Organization ID</Label>
                <Input value={org?.id || ""} disabled />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={updateOrg.isPending || !form.formState.isDirty}
              >
                {updateOrg.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logos</CardTitle>
          <CardDescription>Logos used across apps and printed documents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Parts</p>
              <p className="text-xs text-muted-foreground">Appears on Purchase Order PDFs</p>
            </div>
          </div>
          {org?.logo_url ? (
            <div className="flex items-center gap-4">
              <img
                src={org.logo_url}
                alt="Organization logo"
                className="h-16 max-w-[200px] object-contain rounded border border-border p-2 bg-white"
              />
              <Button variant="outline" size="sm" className="gap-1.5 text-muted-foreground" onClick={handleRemoveLogo}>
                <X className="h-4 w-4" />Remove
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-dashed border-border bg-muted/30">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No logo uploaded yet</p>
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoUpload}
            />
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              <Upload className="h-4 w-4" />
              {uploadingLogo ? "Uploading…" : org?.logo_url ? "Replace Logo" : "Upload Logo"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PNG or SVG recommended. Max 2MB.</p>
          </div>
        </CardContent>
      </Card>

      <PartsPurchaserCard org={org} updateOrg={updateOrg} />

      <Card>
        <CardHeader>
          <CardTitle>Regional Settings</CardTitle>
          <CardDescription>
            Configure timezone, units, and formats
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={form.watch("timezone")}
                  onValueChange={(value) => form.setValue("timezone", value, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="volume_unit">Volume Unit</Label>
                <Select
                  value={form.watch("volume_unit")}
                  onValueChange={(value: any) => form.setValue("volume_unit", value, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gallons">Gallons</SelectItem>
                    <SelectItem value="liters">Liters</SelectItem>
                    <SelectItem value="barrels">Barrels</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature_unit">Temperature Unit</Label>
                <Select
                  value={form.watch("temperature_unit")}
                  onValueChange={(value: any) => form.setValue("temperature_unit", value, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celsius">Celsius</SelectItem>
                    <SelectItem value="fahrenheit">Fahrenheit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_format">Date Format</Label>
                <Select
                  value={form.watch("date_format")}
                  onValueChange={(value) => form.setValue("date_format", value, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={updateOrg.isPending || !form.formState.isDirty}
              >
                {updateOrg.isPending ? "Saving..." : "Save Regional Settings"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
